import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { WEIGHTS, WHATSAPP_NUMBER, inr, kgPrice, unitLabel } from "../data/menu";

export const ProductCard = ({ item, img, onAdd }) => {
  const [qty, setQty] = useState(1);
  const [weightIdx, setWeightIdx] = useState(1);

  const isKg = item.type === "kg";
  const weight = WEIGHTS[weightIdx];
  const unitPrice = isKg ? kgPrice(item, weight.factor) : item.price;
  const lineUnit = isKg ? weight.label : unitLabel(item);

  const add = () => {
    onAdd({
      key: isKg ? `${item.id}:${weight.label}` : item.id,
      id: item.id,
      name: item.name,
      variant: isKg ? weight.label : null,
      unitLabel: lineUnit,
      unitPrice,
      qty,
      img,
    });
    toast.success(`${item.name} added to cart`, { description: `${qty} × ${lineUnit} — ${inr(unitPrice * qty)}` });
    setQty(1);
  };

  if (item.type === "enquiry") {
    return (
      <motion.div
        data-testid={`product-card-${item.id}`}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl border border-dashed border-[#D86A7A]/50 p-5 flex flex-col justify-between shadow-[0_10px_30px_-5px_rgba(61,35,20,0.06)] hover:shadow-[0_18px_40px_-10px_rgba(61,35,20,0.12)] hover:-translate-y-1 transition-all duration-300"
      >
        <div>
          <h3 className="font-display text-lg font-medium text-[#3D2314]">{item.name}</h3>
          <p className="text-sm text-[#8A7364] mt-1">{item.note}</p>
        </div>
        <a
          data-testid={`whatsapp-enquiry-btn-${item.id}`}
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
            "Hello Harshi's Kitchen! I'd like to know the price of Cheese Balls."
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-[#3A6B4C] text-[#3A6B4C] hover:bg-[#EBF2ED] px-5 py-2.5 text-sm font-semibold transition-colors duration-300"
        >
          <MessageCircle size={16} /> Enquire on WhatsApp
        </a>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid={`product-card-${item.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl border border-[#EFE6DC] overflow-hidden shadow-[0_10px_30px_-5px_rgba(61,35,20,0.06)] hover:shadow-[0_18px_40px_-10px_rgba(61,35,20,0.14)] hover:-translate-y-1 transition-all duration-300 group"
    >
      <div className="h-32 sm:h-36 overflow-hidden">
        <img
          src={img}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-base sm:text-lg font-medium text-[#3D2314] leading-snug">{item.name}</h3>
          <span className="shrink-0 text-[#D86A7A] font-bold text-base sm:text-lg">{inr(unitPrice)}</span>
        </div>
        <p className="text-xs text-[#8A7364] mt-1">
          {isKg ? `${inr(item.price)} / kg` : `${inr(item.price)} · ${lineUnit}`}
        </p>

        {isKg && (
          <div className="flex gap-1.5 mt-3">
            {WEIGHTS.map((w, i) => (
              <button
                key={w.label}
                data-testid={`weight-selector-${item.id}-${w.label.replace(" ", "")}`}
                onClick={() => setWeightIdx(i)}
                className={`flex-1 rounded-full px-2 py-1.5 text-xs font-semibold border transition-colors duration-300 ${
                  i === weightIdx
                    ? "bg-[#3A6B4C] border-[#3A6B4C] text-white"
                    : "bg-[#FAF4EC] border-[#E8D8C8] text-[#5A4232] hover:border-[#3A6B4C]"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        )}
        {isKg && (
          <p className="text-xs text-[#3A6B4C] font-medium mt-2">
            {weight.label} — {inr(unitPrice)}
          </p>
        )}

        <div className="flex items-center justify-between mt-4 gap-2">
          <div className="flex items-center rounded-full border border-[#E8D8C8] bg-[#FAF4EC]">
            <button
              data-testid={`quantity-decrease-btn-${item.id}`}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-8 h-8 flex items-center justify-center text-[#5A4232] hover:text-[#D86A7A] transition-colors"
              aria-label="Decrease quantity"
            >
              <Minus size={14} />
            </button>
            <span data-testid={`quantity-display-${item.id}`} className="w-7 text-center text-sm font-bold text-[#3D2314]">
              {qty}
            </span>
            <button
              data-testid={`quantity-increase-btn-${item.id}`}
              onClick={() => setQty((q) => q + 1)}
              className="w-8 h-8 flex items-center justify-center text-[#5A4232] hover:text-[#D86A7A] transition-colors"
              aria-label="Increase quantity"
            >
              <Plus size={14} />
            </button>
          </div>
          <button
            data-testid={`add-to-cart-btn-${item.id}`}
            onClick={add}
            className="rounded-full bg-[#D86A7A] hover:bg-[#C85265] text-white px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold transition-all duration-300 active:scale-95"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const ComboCard = ({ item, img, onAdd }) => {
  const [qty, setQty] = useState(1);
  const add = () => {
    onAdd({ key: item.id, id: item.id, name: item.name, variant: null, unitLabel: "per combo", unitPrice: item.price, qty, img });
    toast.success("Lunch Combo added to cart", { description: `${qty} × ${inr(item.price)}` });
    setQty(1);
  };
  return (
    <motion.div
      data-testid={`product-card-${item.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-3xl bg-[#EBF2ED] border border-[#3A6B4C]/25 shadow-[0_18px_44px_-14px_rgba(58,107,76,0.3)] grid md:grid-cols-2"
    >
      <div className="h-52 md:h-auto overflow-hidden group">
        <img
          src={img}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-6 sm:p-8 flex flex-col justify-center">
        <span className="inline-block w-fit rounded-full bg-[#3A6B4C] text-white text-[0.65rem] font-bold tracking-[0.25em] uppercase px-3 py-1">
          Chef&rsquo;s Special
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#3D2314] mt-3">{item.name}</h3>
        <div className="flex flex-wrap gap-2 mt-4">
          {item.includes.map((inc) => (
            <span key={inc} className="rounded-full bg-white border border-[#3A6B4C]/25 text-[#3A6B4C] text-xs font-semibold px-3 py-1">
              {inc}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
          <span className="font-display text-3xl font-bold text-[#D86A7A]">{inr(item.price)}</span>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-[#3A6B4C]/30 bg-white">
              <button
                data-testid={`quantity-decrease-btn-${item.id}`}
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-9 h-9 flex items-center justify-center text-[#3D2314] hover:text-[#D86A7A] transition-colors"
                aria-label="Decrease quantity"
              >
                <Minus size={15} />
              </button>
              <span data-testid={`quantity-display-${item.id}`} className="w-7 text-center text-sm font-bold text-[#3D2314]">
                {qty}
              </span>
              <button
                data-testid={`quantity-increase-btn-${item.id}`}
                onClick={() => setQty((q) => q + 1)}
                className="w-9 h-9 flex items-center justify-center text-[#3D2314] hover:text-[#D86A7A] transition-colors"
                aria-label="Increase quantity"
              >
                <Plus size={15} />
              </button>
            </div>
            <button
              data-testid={`add-to-cart-btn-${item.id}`}
              onClick={add}
              className="rounded-full bg-[#D86A7A] hover:bg-[#C85265] text-white px-6 py-2.5 text-sm font-semibold transition-all duration-300 active:scale-95 shadow-[0_10px_24px_-8px_rgba(216,106,122,0.55)]"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
